// ===================================
// Mood Garden - Core Logic
// ===================================

// Import SeaCloud SDK
import { llmChatCompletions } from 'seacloud-sdk';

// AI Mood Companion
class MoodCompanion {
  async generateEncouragement(mood) {
    const moodLevel = Math.round(Math.max(1, Math.min(5, mood)));

    // Customized prompts based on mood level
    const prompts = {
      1: 'The user is feeling very down (mood 1/5). Please give a brief encouragement (within 15 words) in a warm, understanding, and supportive tone. Let them feel understood and accompanied.',
      2: 'The user is not feeling great (mood 2/5). Please give a brief comfort (within 15 words) in a gentle, caring tone. Help them feel supported.',
      3: 'The user is feeling calm (mood 3/5). Please give a brief acknowledgment (within 15 words) in a relaxed, friendly tone. Let them feel accompanied.',
      4: 'The user is feeling good (mood 4/5). Please give a brief praise (within 15 words) in a cheerful, affirming tone. Share their joy.',
      5: 'The user is feeling amazing (mood 5/5)! Please give a brief celebration (within 15 words) in an excited, congratulatory tone. Celebrate this wonderful moment together.'
    };

    try {
      const response = await llmChatCompletions({
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'user',
            content: prompts[moodLevel]
          }
        ],
        max_tokens: 100,
        temperature: 0.9
      });

      return response.choices[0].message.content.trim();
    } catch (error) {
      console.error('Failed to generate encouragement:', error);
      // Fallback: return preset encouragement
      const fallbackMessages = {
        1: 'I understand how you feel, everything will get better',
        2: "I'm here with you, you're not alone",
        3: 'Your mood is quite good, keep it up!',
        4: 'You are such a sunshine person!',
        5: 'Amazing! Your mood is absolutely radiant!'
      };
      return fallbackMessages[moodLevel];
    }
  }
}

// Data Management
class MoodDataManager {
  constructor() {
    this.currentDate = new Date();
    this.storageKey = this.getStorageKey();
  }

  getStorageKey() {
    const year = this.currentDate.getFullYear();
    const month = String(this.currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(this.currentDate.getDate()).padStart(2, '0');
    return `mood_garden_${year}-${month}-${day}`;
  }

  loadData() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Failed to load data:', error);
      return [];
    }
  }

  saveData(records) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(records));
      return true;
    } catch (error) {
      console.error('Failed to save data:', error);
      return false;
    }
  }

  addRecord(hour, mood, note = '') {
    const records = this.loadData();
    const existingIndex = records.findIndex(r => r.hour === hour);

    const record = {
      hour,
      mood,
      note: note.trim(),
      updatedAt: new Date().toISOString()
    };

    if (existingIndex >= 0) {
      records[existingIndex] = record;
    } else {
      records.push(record);
    }

    records.sort((a, b) => a.hour - b.hour);
    return this.saveData(records);
  }

  getRecord(hour) {
    const records = this.loadData();
    return records.find(r => r.hour === hour) || null;
  }

  getAverageMood() {
    const records = this.loadData();
    if (records.length === 0) return 3;

    const sum = records.reduce((acc, r) => acc + r.mood, 0);
    return Math.round(sum / records.length);
  }

  getLatestMood() {
    const records = this.loadData();
    if (records.length === 0) return 3;

    const sorted = [...records].sort((a, b) =>
      new Date(b.updatedAt) - new Date(a.updatedAt)
    );
    return sorted[0].mood;
  }
}

// Flower Visual Manager - FlowerAvatar V2 (Healing Purple Flower)
class FlowerVisualizer {
  constructor(containerId) {
    this.flower = new FlowerAvatar_v2(containerId);
    this.currentMood = 3;

    // Initialize to mood 3
    this.updateFlower(3);
  }

  updateFlower(mood) {
    const targetMood = Math.max(1, Math.min(5, mood));
    this.currentMood = targetMood;
    this.flower.setMood(targetMood);
  }

  bloom() {
    this.flower.bloom();
  }
}

// Chart Management - Optimized: Real Y-axis mapping + NOW minute precision
class ChartRenderer {
  constructor(svgId) {
    console.log('🔧 ChartRenderer initialization started...');

    this.svg = document.getElementById(svgId);
    this.pointsContainer = document.getElementById('dataPointsContainer');
    this.connectionLine = document.getElementById('connectionLine');
    this.nowIndicator = document.getElementById('nowIndicator');
    this.nowLine = document.getElementById('nowLine');
    this.nowText = document.getElementById('nowText');
    this.nowPoint = document.getElementById('nowPoint');

    // Chart dimensions (extended time axis)
    this.chartWidth = 455;  // X-axis range (505-50), increased from 410 to 455
    this.chartHeight = 180; // Y-axis mood value range (235-55)
    this.xOffset = 50;      // X-axis start, reduced from 55 to 50
    this.yOffset = 55;      // Y-axis start (mood value 5 position)

    console.log('📊 Chart dimensions:', {
      chartWidth: this.chartWidth,
      chartHeight: this.chartHeight,
      xOffset: this.xOffset,
      yOffset: this.yOffset
    });

    // Slider elements
    this.sliderHandle = document.getElementById('sliderHandle');
    this.sliderTrack = document.getElementById('sliderTrack');

    // Current preview mood
    this.currentPreviewMood = null;

    console.log('⏰ Preparing to update NOW indicator...');
    this.updateNowIndicator();
    setInterval(() => this.updateNowIndicator(), 60000);

    // Bind slider drag events
    this.bindSliderDrag();

    console.log('✅ ChartRenderer initialization complete');
  }

  // Bind slider drag events
  bindSliderDrag() {
    if (!this.sliderHandle) return;

    let isDragging = false;
    let animationFrameId = null;
    let pendingY = null;

    const handleStart = (e) => {
      isDragging = true;
      this.sliderHandle.style.cursor = 'grabbing';
      e.preventDefault();
      e.stopPropagation();
    };

    const handleMove = (e) => {
      if (!isDragging) return;

      const svgRect = this.svg.getBoundingClientRect();
      const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;
      const relativeY = clientY - svgRect.top;

      // Convert screen coordinates to SVG coordinates (updated for viewBox height 280)
      const svgY = (relativeY / svgRect.height) * 280;

      // Constrain within valid mood value range (55-235, corresponding to mood 5 to mood 1)
      const constrainedY = Math.max(55, Math.min(235, svgY));

      // Save pending Y coordinate
      pendingY = constrainedY;

      // Use requestAnimationFrame to optimize performance
      if (!animationFrameId) {
        animationFrameId = requestAnimationFrame(() => {
          if (pendingY !== null) {
            // Calculate mood value
            const mood = this.yToMood(pendingY);

            // Get NOW line X position
            const nowX = parseFloat(this.nowLine.getAttribute('x1')) || 0;

            // Update slider position
            this.sliderHandle.setAttribute('transform', `translate(${nowX}, ${pendingY})`);

            // Update flower preview (throttled: only update when mood value changes)
            const roundedMood = Math.round(mood * 10) / 10; // Keep 1 decimal place
            if (roundedMood !== this.currentPreviewMood) {
              this.currentPreviewMood = roundedMood;
              window.moodApp?.flowerVisualizer.updateFlower(mood);
            }

            pendingY = null;
          }
          animationFrameId = null;
        });
      }

      e.preventDefault();
      e.stopPropagation();
    };

    const handleEnd = () => {
      if (!isDragging) return;
      isDragging = false;
      this.sliderHandle.style.cursor = 'grab';

      // Cancel pending animation frame
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }

      // Keep current preview state after releasing, no popup
      // Slider is purely a preview tool, click chart area to record mood
    };

    // Mouse events
    this.sliderHandle.addEventListener('mousedown', handleStart);
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);

    // Touch events
    this.sliderHandle.addEventListener('touchstart', handleStart, { passive: false });
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);

    // Set initial cursor style
    this.sliderHandle.style.cursor = 'grab';
  }

  // Y coordinate to mood value
  yToMood(y) {
    // Based on grid line positions: mood 5 at y=55, mood 1 at y=235
    const minY = 55;  // Mood value 5 position (grid line)
    const maxY = 235; // Mood value 1 position (bottom axis)
    const clampedY = Math.max(minY, Math.min(maxY, y));
    const mood = 5 - ((clampedY - minY) / (maxY - minY)) * 4;
    return Math.max(1, Math.min(5, mood));
  }

  hourToX(hour) {
    return this.xOffset + (hour / 24) * this.chartWidth;
  }

  moodToY(mood) {
    // Linear mapping from mood value to Y coordinate
    // mood 5 -> Y=55 (top grid line)
    // mood 1 -> Y=235 (bottom axis)
    const minY = 55;
    const maxY = 235;
    return maxY - ((mood - 1) / 4) * (maxY - minY);
  }

  updateNowIndicator() {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const decimalHour = hours + minutes / 60;

    const x = this.hourToX(decimalHour);

    console.log('=== NOW Indicator Update ===');
    console.log('Current time:', `${hours}:${String(minutes).padStart(2, '0')}`);
    console.log('Decimal hour:', decimalHour.toFixed(4));
    console.log('Calculated X coordinate:', x.toFixed(2));
    console.log('chartWidth:', this.chartWidth, '| xOffset:', this.xOffset);
    console.log('Expected X calculation:', `${this.xOffset} + (${decimalHour.toFixed(2)} / 24) × ${this.chartWidth} = ${x.toFixed(2)}`);
    console.log('---');
    console.log('Time label reference points:');
    console.log('  0:00 should be at X=50, calculated:', this.hourToX(0));
    console.log('  6:00 should be at X=163.75, calculated:', this.hourToX(6));
    console.log(' 12:00 should be at X=277.5, calculated:', this.hourToX(12));
    console.log(' 18:00 should be at X=391.25, calculated:', this.hourToX(18));
    console.log(' 24:00 should be at X=505, calculated:', this.hourToX(24));

    this.nowLine.setAttribute('x1', x);
    this.nowLine.setAttribute('x2', x);
    this.nowText.setAttribute('x', x);

    // Update slider X position
    if (this.sliderHandle) {
      const currentTransform = this.sliderHandle.getAttribute('transform');
      const yMatch = currentTransform ? currentTransform.match(/translate\([\d.]+,\s*([\d.]+)\)/) : null;
      const currentY = yMatch ? parseFloat(yMatch[1]) : 130;
      this.sliderHandle.setAttribute('transform', `translate(${x}, ${currentY})`);
    }

    this.nowIndicator.classList.add('visible');
    this.updateNowPoint(hours, x);
  }

  updateNowPoint(hour, x) {
    this.nowPoint.classList.remove('visible');
  }

  renderChart(records, onPointClick) {
    // Clear existing points
    this.pointsContainer.innerHTML = '';

    const recordedPoints = [];
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    const nowX = this.hourToX(currentHour + currentMinutes / 60);

    for (let hour = 0; hour < 24; hour++) {
      const record = records.find(r => r.hour === hour);
      const isCurrentHour = (hour === currentHour);
      const pointX = isCurrentHour ? nowX : this.hourToX(hour);
      const isReachable = hour <= currentHour;

      if (isReachable) {
        const width = this.chartWidth / 24;
        const clickArea = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        clickArea.classList.add('data-point-area');
        clickArea.setAttribute('x', this.hourToX(hour));
        clickArea.setAttribute('y', this.yOffset);
        clickArea.setAttribute('width', width);
        clickArea.setAttribute('height', this.chartHeight);
        clickArea.setAttribute('fill', 'transparent');
        clickArea.style.cursor = 'pointer';

        clickArea.addEventListener('click', () => onPointClick(hour));
        this.pointsContainer.appendChild(clickArea);
      }

      if (record) {
        const y = this.moodToY(record.mood);
        recordedPoints.push({ x: pointX, y, hour: record.hour });

        const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        dot.classList.add('mood-dot');
        dot.setAttribute('cx', pointX);
        dot.setAttribute('cy', y);
        dot.setAttribute('r', '6');
        dot.setAttribute('fill', 'var(--accent-rose)');
        dot.setAttribute('opacity', '0.8');
        dot.style.pointerEvents = 'none';
        this.pointsContainer.appendChild(dot);
      }
    }

    if (recordedPoints.length > 1) {
      // Default: do not show connection line, only show when Mood Bloom button is clicked
      // const pathD = this.createLinePath(recordedPoints);
      // this.connectionLine.setAttribute('d', pathD);
      // this.connectionLine.style.display = 'block';
      this.connectionLine.style.display = 'none';
    } else {
      this.connectionLine.style.display = 'none';
    }

    this.updateNowPoint(currentHour, nowX);
  }

  createLinePath(points) {
    if (points.length === 0) return '';
    if (points.length === 1) return `M ${points[0].x},${points[0].y}`;

    // Use smooth Bezier curves to create natural trajectory
    let path = `M ${points[0].x},${points[0].y}`;

    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];

      // Calculate control points to create smooth curve transitions
      // Control point at middle X position between two points, Y value maintains previous point height
      const cpX = (prev.x + curr.x) / 2;

      // Use quadratic Bezier curve (Q command) to create smooth transition
      // Q controlX,controlY endX,endY
      path += ` Q ${cpX},${prev.y} ${cpX},${(prev.y + curr.y) / 2}`;
      path += ` Q ${cpX},${curr.y} ${curr.x},${curr.y}`;
    }

    return path;
  }

  // Animate mood trajectory drawing while updating flower
  animateMoodPath(records) {
    if (records.length === 0) return;

    // Hide connection line, prepare for animation (mood dots remain visible)
    this.connectionLine.style.display = 'none';

    // Sort records by hour
    const sortedRecords = [...records].sort((a, b) => a.hour - b.hour);

    // Prepare point data
    const points = sortedRecords.map(record => {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinutes = now.getMinutes();
      const isCurrentHour = (record.hour === currentHour);
      const nowX = this.hourToX(currentHour + currentMinutes / 60);
      const pointX = isCurrentHour ? nowX : this.hourToX(record.hour);
      const y = this.moodToY(record.mood);

      return { x: pointX, y, mood: record.mood, hour: record.hour };
    });

    // Generate full path
    const fullPath = this.createLinePath(points);
    this.connectionLine.setAttribute('d', fullPath);
    this.connectionLine.style.display = 'block';

    // Get total path length
    const pathLength = this.connectionLine.getTotalLength();

    // Set initial state: path completely hidden
    this.connectionLine.style.strokeDasharray = pathLength;
    this.connectionLine.style.strokeDashoffset = pathLength;

    // Animation parameters
    const totalDuration = points.length * 800; // Total animation duration
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / totalDuration, 1);

      // Update path drawing progress (from hidden to fully visible)
      const currentOffset = pathLength * (1 - progress);
      this.connectionLine.style.strokeDashoffset = currentOffset;

      // Smoothly interpolate flower mood based on progress
      if (points.length > 1) {
        // Calculate which two points we're between
        const totalSegments = points.length - 1;
        const currentSegment = progress * totalSegments;
        const segmentIndex = Math.floor(currentSegment);
        const segmentProgress = currentSegment - segmentIndex;

        if (segmentIndex < totalSegments) {
          // Linear interpolation between two points
          const startPoint = points[segmentIndex];
          const endPoint = points[segmentIndex + 1];

          // Smoothly interpolate mood value
          const interpolatedMood = startPoint.mood + (endPoint.mood - startPoint.mood) * segmentProgress;

          if (window.moodApp?.flowerVisualizer) {
            window.moodApp.flowerVisualizer.updateFlower(interpolatedMood);
          }
        } else if (segmentIndex === totalSegments) {
          // Last point
          const lastPoint = points[points.length - 1];
          if (window.moodApp?.flowerVisualizer) {
            window.moodApp.flowerVisualizer.updateFlower(lastPoint.mood);
          }
        }
      } else if (points.length === 1) {
        // Only one point
        if (window.moodApp?.flowerVisualizer) {
          window.moodApp.flowerVisualizer.updateFlower(points[0].mood);
        }
      }

      // Continue animation
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Animation complete, clear dasharray style
        this.connectionLine.style.strokeDasharray = 'none';
        this.connectionLine.style.strokeDashoffset = '0';
      }
    };

    // Start animation
    requestAnimationFrame(animate);
  }

  getMoodColor(mood) {
    const colors = {
      1: 'var(--mood-1)',
      2: 'var(--mood-2)',
      3: 'var(--mood-3)',
      4: 'var(--mood-4)',
      5: 'var(--mood-5)'
    };
    return colors[mood] || 'var(--mood-3)';
  }
}

// Modal Management
class ModalManager {
  constructor() {
    this.overlay = document.getElementById('modalOverlay');
    this.content = document.getElementById('modalContent');
    this.closeBtn = document.getElementById('modalClose');
    this.timeDisplay = document.getElementById('modalTime');
    this.moodSelector = document.getElementById('moodSelector');
    this.noteInput = document.getElementById('noteInput');
    this.noteCounter = document.getElementById('noteCounter');
    this.saveBtn = document.getElementById('modalSaveBtn');

    this.selectedMood = null;
    this.currentHour = null;
    this.onSaveCallback = null;

    this.bindEvents();
  }

  bindEvents() {
    // Close on overlay click
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
      }
    });

    // Close button
    this.closeBtn.addEventListener('click', () => this.close());

    // Mood selection
    this.moodSelector.addEventListener('click', (e) => {
      const option = e.target.closest('.mood-option');
      if (!option) return;

      const mood = parseInt(option.dataset.mood);
      this.selectMood(mood);
    });

    // Note input
    this.noteInput.addEventListener('input', () => {
      this.noteCounter.textContent = this.noteInput.value.length;
    });

    // Save button
    this.saveBtn.addEventListener('click', () => {
      this.save();
    });

    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      if (!this.overlay.classList.contains('active')) return;

      if (e.key === 'Escape') {
        this.close();
      } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        if (!this.saveBtn.disabled) {
          this.save();
        }
      }
    });
  }

  open(hour, existingRecord = null, onSave = null) {
    this.currentHour = hour;
    this.onSaveCallback = onSave;

    const formattedHour = String(hour).padStart(2, '0');
    this.timeDisplay.textContent = `${formattedHour}:00 - ${formattedHour}:59`;

    if (existingRecord) {
      this.selectMood(existingRecord.mood);
      this.noteInput.value = existingRecord.note || '';
      this.noteCounter.textContent = this.noteInput.value.length;
    } else {
      this.selectedMood = null;
      this.noteInput.value = '';
      this.noteCounter.textContent = '0';
      this.saveBtn.disabled = true;

      const options = this.moodSelector.querySelectorAll('.mood-option');
      options.forEach(opt => opt.classList.remove('selected'));
    }

    this.overlay.classList.add('active');
    document.body.style.overflow = 'hidden';

    setTimeout(() => {
      if (!this.selectedMood) {
        const firstMoodBtn = this.moodSelector.querySelector('.mood-option');
        if (firstMoodBtn) firstMoodBtn.focus();
      } else {
        this.noteInput.focus();
      }
    }, 400);
  }

  close() {
    this.overlay.classList.remove('active');
    document.body.style.overflow = '';

    setTimeout(() => {
      this.reset();
    }, 300);
  }

  selectMood(mood) {
    this.selectedMood = mood;

    const options = this.moodSelector.querySelectorAll('.mood-option');
    options.forEach(opt => {
      if (parseInt(opt.dataset.mood) === mood) {
        opt.classList.add('selected');
      } else {
        opt.classList.remove('selected');
      }
    });

    this.saveBtn.disabled = false;
  }

  reset() {
    this.selectedMood = null;
    this.currentHour = null;
    this.noteInput.value = '';
    this.noteCounter.textContent = '0';
    this.saveBtn.disabled = true;

    const options = this.moodSelector.querySelectorAll('.mood-option');
    options.forEach(opt => opt.classList.remove('selected'));
  }

  save() {
    if (!this.selectedMood || this.currentHour === null) {
      return;
    }

    const record = {
      hour: this.currentHour,
      mood: this.selectedMood,
      note: this.noteInput.value.trim()
    };

    if (this.onSaveCallback) {
      this.onSaveCallback(record);
    }

    this.close();
  }
}

// Main Application
class MoodGardenApp {
  constructor() {
    this.dataManager = new MoodDataManager();
    this.moodCompanion = new MoodCompanion();
    this.flowerVisualizer = new FlowerVisualizer('flowerContainer');
    this.chartRenderer = new ChartRenderer('chartSvg');
    this.modalManager = new ModalManager();

    this.init();
  }

  init() {
    this.render();
    this.updateStatusHint();
    this.updateDateDisplay();

    const ctaBtn = document.getElementById('ctaBtn');
    ctaBtn.addEventListener('click', () => {
      // Get all mood records
      const records = this.dataManager.loadData();

      if (records.length > 0) {
        // Play animation: draw curve from start while flower changes
        this.chartRenderer.animateMoodPath(records);

        // Trigger flower bloom effect after animation
        // Total animation duration = number of points × 800ms + 300ms buffer
        setTimeout(() => {
          this.flowerVisualizer.bloom();
        }, records.length * 800 + 300);
      } else {
        // No records, only trigger flower bloom
        this.flowerVisualizer.bloom();
      }
    });

    // Only update flower when there are records, otherwise keep initial mood 3
    const records = this.dataManager.loadData();
    console.log('📊 Number of records on init:', records.length);
    if (records.length > 0) {
      const avgMood = this.dataManager.getLatestMood();
      console.log('📊 Latest mood value:', avgMood);
      console.log('📊 All records:', records.map(r => `Hour ${r.hour}: Mood ${r.mood}`).join(', '));
      this.flowerVisualizer.updateFlower(avgMood);
    } else {
      console.log('📊 No records, keeping initial mood 3');
    }
  }

  updateStatusHint() {
    const statusMood = document.getElementById('statusMood');
    if (!statusMood) return;

    const currentHour = new Date().getHours();
    const record = this.dataManager.getRecord(currentHour);

    if (record) {
      const moodNames = {
        1: 'Feeling Low',
        2: 'Just Okay',
        3: 'Calm',
        4: 'Happy',
        5: 'Amazing'
      };
      statusMood.textContent = `Now: ${moodNames[record.mood]} · ${record.mood}/5`;
    } else {
      statusMood.textContent = 'Now: Not Recorded';
    }
  }

  updateDateDisplay() {
    const dateDisplay = document.getElementById('dateDisplay');
    const now = new Date();

    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    };

    const formatter = new Intl.DateTimeFormat('en-US', options);
    dateDisplay.textContent = formatter.format(now);
  }

  render() {
    const records = this.dataManager.loadData();

    this.chartRenderer.renderChart(records, (hour) => {
      this.openRecordModal(hour);
    });
  }

  openRecordModal(hour) {
    const existingRecord = this.dataManager.getRecord(hour);

    this.modalManager.open(hour, existingRecord, (record) => {
      this.saveRecord(record);
    });
  }

  saveRecord(record) {
    const success = this.dataManager.addRecord(record.hour, record.mood, record.note);

    if (success) {
      this.render();
      this.updateStatusHint();

      const latestMood = this.dataManager.getLatestMood();
      this.flowerVisualizer.updateFlower(latestMood);
      this.flowerVisualizer.bloom();

      // Generate and display encouragement
      this.showEncouragement(record.mood);
    }
  }

  async showEncouragement(mood) {
    try {
      // Call LLM to dynamically generate encouragement
      const encouragement = await this.moodCompanion.generateEncouragement(mood);

      // Display encouragement (auto-hide after 4 seconds)
      this.showEncouragementMessage(encouragement, 'success');

      setTimeout(() => {
        this.hideEncouragementMessage();
      }, 4000);
    } catch (error) {
      console.error('Failed to display encouragement:', error);
      // Silent handling, don't affect user experience even if it fails
    }
  }

  showEncouragementMessage(message, type = 'success') {
    // Find or create encouragement container
    let container = document.getElementById('encouragementContainer');

    if (!container) {
      container = document.createElement('div');
      container.id = 'encouragementContainer';
      container.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(255, 255, 255, 0.98);
        padding: 24px 32px;
        border-radius: 16px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
        font-size: 18px;
        color: #333;
        max-width: 80%;
        text-align: center;
        z-index: 10000;
        opacity: 0;
        transition: opacity 0.3s ease;
        backdrop-filter: blur(10px);
        border: 1px solid rgba(200, 180, 220, 0.3);
      `;
      document.body.appendChild(container);
    }

    container.textContent = message;

    // Set style based on type
    if (type === 'loading') {
      container.style.background = 'rgba(240, 235, 255, 0.98)';
      container.style.color = '#7B68B6';
    } else {
      container.style.background = 'rgba(255, 255, 255, 0.98)';
      container.style.color = '#333';
    }

    // Fade in
    setTimeout(() => {
      container.style.opacity = '1';
    }, 10);
  }

  hideEncouragementMessage() {
    const container = document.getElementById('encouragementContainer');
    if (container) {
      container.style.opacity = '0';
      setTimeout(() => {
        container.remove();
      }, 300);
    }
  }
}

// Initialize application
document.addEventListener('DOMContentLoaded', () => {
  window.moodApp = new MoodGardenApp();
});
